const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

export const INITIAL_DISCIPLINE_FORM = {
  nom_discipline: "",
  description_discipline: "",
  contenu_discipline: "",
  image_discipline: "",
  imageFile: null,
  ordre_discipline: 0,
};

export const safeJsonParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const isDemoUser = (user) => Number(user?.role_id) === 4;

export const formatNumber = (value) =>
  new Intl.NumberFormat("fr-FR").format(Number(value || 0));

export const isExternalURL = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

export const getMediaUrl = (url) => {
  if (!url) return "";
  if (isExternalURL(url)) return url;
  return `${BASE_URL}${url.startsWith("/") ? url : `/${url}`}`;
};

export const stripHtml = (html = "") =>
  String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const truncateText = (value = "", limit = 160) => {
  const text = stripHtml(value);

  if (text.length <= limit) return text;

  return `${text.slice(0, limit).trim()}...`;
};

export const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const filterAndSortDisciplines = (disciplines = [], filters = {}) => {
  const query = normalizeText(filters.query);

  let result = [...disciplines];

  if (query) {
    result = result.filter((discipline) => {
      const haystack = normalizeText(
        [
          discipline.nom_discipline,
          discipline.description_discipline,
          stripHtml(discipline.contenu_discipline),
        ]
          .filter(Boolean)
          .join(" ")
      );

      return haystack.includes(query);
    });
  }

  result.sort((a, b) => {
    if (filters.sortOrder === "ordre_desc") {
      return Number(b.ordre_discipline || 0) - Number(a.ordre_discipline || 0);
    }

    if (filters.sortOrder === "name_asc") {
      return normalizeText(a.nom_discipline).localeCompare(
        normalizeText(b.nom_discipline)
      );
    }

    if (filters.sortOrder === "name_desc") {
      return normalizeText(b.nom_discipline).localeCompare(
        normalizeText(a.nom_discipline)
      );
    }

    return Number(a.ordre_discipline || 0) - Number(b.ordre_discipline || 0);
  });

  return result;
};

export const buildDisciplinesStats = (
  disciplines = [],
  filteredDisciplines = []
) => {
  const withImage = disciplines.filter(
    (discipline) => Boolean(discipline.image_discipline)
  ).length;

  const withContent = disciplines.filter(
    (discipline) => Boolean(stripHtml(discipline.contenu_discipline))
  ).length;

  const maxOrder = disciplines.reduce((max, discipline) => {
    const ordre = Number(discipline.ordre_discipline || 0);
    return ordre > max ? ordre : max;
  }, 0);

  return {
    total: disciplines.length,
    filtered: filteredDisciplines.length,
    withImage,
    withContent,
    maxOrder,
  };
};

export const normalizeDisciplinePayload = (formData) => ({
  ...formData,
  nom_discipline: String(formData.nom_discipline || "").trim(),
  description_discipline: String(
    formData.description_discipline || ""
  ).trim(),
  contenu_discipline: String(formData.contenu_discipline || "").trim(),
  image_discipline: String(formData.image_discipline || "").trim(),
  ordre_discipline:
    formData.ordre_discipline === "" ||
    formData.ordre_discipline === null ||
    formData.ordre_discipline === undefined
      ? 0
      : Number(formData.ordre_discipline),
});

export const validateDisciplineForm = (formData) => {
  if (!String(formData.nom_discipline || "").trim()) {
    return "Le nom de la discipline est obligatoire.";
  }

  if (!String(formData.description_discipline || "").trim()) {
    return "La description courte est obligatoire.";
  }

  if (
    formData.ordre_discipline !== "" &&
    Number(formData.ordre_discipline) < 0
  ) {
    return "L'ordre d'affichage doit être un nombre positif.";
  }

  return null;
};

export const buildInitialFormFromDiscipline = (discipline) => {
  if (!discipline) return INITIAL_DISCIPLINE_FORM;

  return {
    ...INITIAL_DISCIPLINE_FORM,
    ...discipline,
    imageFile: null,
    ordre_discipline: discipline.ordre_discipline ?? 0,
  };
};